import type { BaremeApplicable, CharteSigneeResume, ContactDetail } from '@rh/shared';

/**
 * Dernière lecture du panneau de contact, en mémoire de l'onglet seulement
 * (docs/UI.md § Écran 3, mode hors ligne). Si le réseau coupe en cours de
 * saisie, le panneau se réaffiche depuis elle. Rien n'en va sur le disque : le
 * nom et le téléphone du candidat ne quittent jamais la mémoire, vidée à la
 * déconnexion et au changement de compte.
 */

/** Ce que le panneau affiche de la fiche : de quoi reconnaître le candidat, rien de plus. */
export interface FicheEnMemoire {
  id: string;
  prenom: string | null;
  nom: string | null;
  telephone: string | null;
  annonceIntitule: string | null;
  potentiel: number | null;
  anonymiseeLe: string | null;
}

export interface LieuEnMemoire {
  id: string;
  libelle: string;
}

const contacts = new Map<string, ContactDetail>();
const fiches = new Map<string, FicheEnMemoire>();
const baremes = new Map<string, BaremeApplicable>();
const chartes = new Map<string, CharteSigneeResume[]>();
let lieux: ReadonlyArray<LieuEnMemoire> | undefined;

export const memoire = {
  retenirContact(contact: ContactDetail): void {
    contacts.set(contact.id, contact);
  },
  contact(id: string): ContactDetail | undefined {
    return contacts.get(id);
  },
  retenirFiche(fiche: FicheEnMemoire): void {
    // On ne garde que les champs affichés, quelle que soit la forme reçue.
    fiches.set(fiche.id, {
      id: fiche.id,
      prenom: fiche.prenom,
      nom: fiche.nom,
      telephone: fiche.telephone,
      annonceIntitule: fiche.annonceIntitule,
      potentiel: fiche.potentiel,
      anonymiseeLe: fiche.anonymiseeLe,
    });
  },
  fiche(candidatureId: string): FicheEnMemoire | undefined {
    return fiches.get(candidatureId);
  },
  retenirBareme(candidatureId: string, bareme: BaremeApplicable): void {
    baremes.set(candidatureId, bareme);
  },
  bareme(candidatureId: string): BaremeApplicable | undefined {
    return baremes.get(candidatureId);
  },
  retenirLieux(liste: ReadonlyArray<LieuEnMemoire>): void {
    lieux = liste.map((l) => ({ id: l.id, libelle: l.libelle }));
  },
  lieux(): ReadonlyArray<LieuEnMemoire> | undefined {
    return lieux;
  },
  /** Reprise de la charte signée (tabac et alcool) : relue hors ligne depuis la dernière lecture. */
  retenirChartes(candidatureId: string, liste: CharteSigneeResume[]): void {
    chartes.set(candidatureId, liste);
  },
  chartes(candidatureId: string): CharteSigneeResume[] | undefined {
    return chartes.get(candidatureId);
  },
  vider(): void {
    contacts.clear();
    fiches.clear();
    baremes.clear();
    chartes.clear();
    lieux = undefined;
  },
};
